import { useState } from "react";
import { useDispatchApp } from "../../../lib/redux";
import { startGetPayInvoiceUrl } from "../../../actions/payments";
import { useTranslationApp } from "../../../lib/i18next";
import { DEFAULT_TABLE_PAGES } from "../../../utils/constants";
import Box from "@mui/material/Box";
import TableContainer from "../../../components/Tables/TableContainer";
import {
  ADS_SUBSCRIPTION_TITLE,
  BILLING_AND_PAYMENTS_TABLE_ARIA_LABEL,
  COLUMN_NAME_ACTION,
  COLUMN_NAME_DUE_DATE,
  COLUMN_NAME_PAID_DATE,
  COLUMN_NAME_REASON,
  COLUMN_NAME_STATUS,
  COLUMN_NAME_VALUE,
  DATE_SHORT,
  EMPTY_RESULTS_SHOW,
  PURCHASE_CREDITS,
  PURCHASE_VIDEOS,
  SPEND_FEE,
  VALUE_WITH_CURRENCY,
} from "../../../i18n/keysTranslations";
import TableCell from "./TableCell";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import { capitalizeFirstLetter } from "../../../utils/string";
import StatusInvoice from "./StatusInvoice";
import ActionsInvoicesTable from "./ActionsInvoicesTable";

const InvoicesTable = ({ data = {}, loading, onSelectInvoice = () => {} }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(DEFAULT_TABLE_PAGES);
  const [payInvoiceLoadingKey, setPayInvoiceLoadingKey] = useState(null);

  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();

  const onPayInvoice = async (invoiceKey) => {
    setPayInvoiceLoadingKey(invoiceKey);
    try {
      const paymentIntent = data[invoiceKey]?.paymentID ?? invoiceKey;
      const result = await dispatch(
        startGetPayInvoiceUrl({ paymentIntent, coldBoot: false })
      );
      if (result?.ok && result?.data?.url) {
        window.location.href = result.data.url;
      }
    } finally {
      setPayInvoiceLoadingKey(null);
    }
  };

  const onChangePage = (newPage) => {
    setPage(newPage);
  };

  const keysSorted = Object.keys(data).sort(
    (a, b) => data[b].dueDate?.getTime() - data[a].dueDate?.getTime()
  );

  if (keysSorted.length === 0) return null;

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      <TableContainer
        page={page}
        loading={loading}
        showEmptyText={false}
        onPageChange={onChangePage}
        rowsPerPage={rowsPerPage}
        keys={keysSorted}
        ariaLabel={t(BILLING_AND_PAYMENTS_TABLE_ARIA_LABEL)}
        sx={{ borderRadius: "0px !important" }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ pl: "16px !important" }} isHeader={true}>
              {t(COLUMN_NAME_PAID_DATE)}
            </TableCell>
            <TableCell isHeader={true}>{t(COLUMN_NAME_DUE_DATE)}</TableCell>
            <TableCell isHeader={true}>{t(COLUMN_NAME_VALUE)}</TableCell>
            <TableCell isHeader={true}>{t(COLUMN_NAME_REASON)}</TableCell>
            <TableCell isHeader={true}>{t(COLUMN_NAME_STATUS)}</TableCell>
            <TableCell isHeader={true} resizable={false}>
              {t(COLUMN_NAME_ACTION)}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(rowsPerPage > 0
            ? keysSorted.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
              )
            : keysSorted
          ).map((key, index) => {
            const isLastRow = index === keysSorted.length - 1;
            const subscription = data[key].subscription;
            const reasonLabel =
              subscription === "credits"
                ? t(PURCHASE_CREDITS)
                : subscription === "videos"
                  ? t(PURCHASE_VIDEOS)
                  : subscription === "spendFee"
                    ? t(SPEND_FEE)
                    : subscription === "specialist"
                      ? "Add-On Marketing Specialist"
                      : subscription === "featureAddon"
                        ? data[key].items?.[0]?.description || "Add-on"
                        : t(ADS_SUBSCRIPTION_TITLE, {
                            value: capitalizeFirstLetter(subscription),
                          });
            return (
              <TableRow key={key}>
                <TableCell
                  indexRow={index}
                  sx={{ pl: "16px !important" }}
                  isLastRow={isLastRow}
                >
                  {!data[key].paidDate
                    ? "-"
                    : t(DATE_SHORT, {
                        date: data[key].paidDate,
                      })}
                </TableCell>
                <TableCell indexRow={index} isLastRow={isLastRow}>
                  {!data[key].dueDate
                    ? "-"
                    : t(DATE_SHORT, {
                        date: data[key].dueDate,
                      })}
                </TableCell>
                <TableCell indexRow={index} isLastRow={isLastRow}>
                  {t(VALUE_WITH_CURRENCY, {
                    value: data[key].value,
                    currency: data[key]?.currency?.toUpperCase() || "USD",
                  })}
                </TableCell>
                <TableCell indexRow={index} isLastRow={isLastRow}>
                  {reasonLabel}
                </TableCell>
                <TableCell indexRow={index} isLastRow={isLastRow}>
                  <StatusInvoice isPaid={data[key].isPaid} />
                </TableCell>
                <TableCell indexRow={index} isLastRow={isLastRow}>
                  <ActionsInvoicesTable
                    data={data[key]}
                    keyInvoice={key}
                    payLoading={payInvoiceLoadingKey === key}
                    onSelectInvoice={onSelectInvoice}
                    onPayInvoice={onPayInvoice}
                  />
                </TableCell>
              </TableRow>
            );
          })}
          {keysSorted.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} sx={{ textAlign: "center" }}>
                {t(EMPTY_RESULTS_SHOW)}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </TableContainer>
    </Box>
  );
};

export default InvoicesTable;
